import { useEffect, useRef } from 'react';

import { layout, litNodes } from '../lib/constellation.js';

/**
 * The ground the whole app sits on.
 *
 * Three layers, in this order:
 *
 *   1. a gradient mesh — four or five very large, very slow pools of colour,
 *      rendered tiny and scaled up so the blur is free rather than a filter;
 *   2. the constellation — the app's own content as a node graph, drifting,
 *      with the parts you have finished lit in gold;
 *   3. a hairline grid and a film grain, both in CSS, painted over the top.
 *
 * The point of the mesh is colour that is not navy-and-gold. The point of the
 * constellation is that the background is *about something*: it is the twenty-
 * eight chapters and ninety-six verses, wired by relationships that already
 * exist in the data, and it fills in with gold as you work through them. The
 * point of the grid is that without it a slow gradient reads as a lava lamp;
 * one crisp geometric layer is what makes the whole thing read as precise.
 *
 * What keeps it cheap:
 *   - the mesh is drawn at about an eighth scale — a few thousand pixels a
 *     frame, upscaled by the compositor;
 *   - the layout is computed once, in an effect after first paint, and cached;
 *   - the loop runs at 30fps, not 60; ambient motion gains nothing from the
 *     extra frames and the battery notices;
 *   - it stops dead while the tab is hidden;
 *   - under `prefers-reduced-motion` it paints a single frame and never loops.
 */

/**
 * Each part of the app gets its own light.
 *
 * Four dark colours per zone, added together on a near-black ground, so these
 * are values you would call almost black on their own — anything brighter and
 * the text starts fighting the wallpaper. Every set keeps one cool anchor so
 * the app never stops feeling like the same product.
 */
const ZONES = {
  home: ['#2b2f6e', '#4a3a86', '#175a62', '#6b4a1e'],
  story: ['#6b4520', '#7a3a2a', '#3a2a5e', '#2a4a5e'],
  practice: ['#4a2f80', '#6a3a72', '#23305e', '#2f5560'],
  flashcards: ['#1c3a6e', '#2a4d7a', '#3b3a72', '#17565e'],
  people: ['#6b2a4a', '#4a2a63', '#2a3a68', '#5e4a24'],
  saved: ['#14564e', '#1c4a6a', '#3a3a72', '#5e5220'],
  chat: ['#14504f', '#1b3a6b', '#45357e', '#2a5a5a'],
  paths: ['#2b2f6e', '#4a3a86', '#175a62', '#6b4a1e'],
};

const FPS = 30;
const ZONE_FADE = 0.9; // seconds to cross from one zone's light to the next
const MAX_PULSES = 5;

const toRgb = (hex) => [
  parseInt(hex.slice(1, 3), 16),
  parseInt(hex.slice(3, 5), 16),
  parseInt(hex.slice(5, 7), 16),
];

/** Where blob `i` is at time `t` — a lissajous, so it never repeats visibly. */
const blobAt = (i, t) => ({
  x: 0.5 + 0.42 * Math.sin(t * (0.031 + i * 0.008) + i * 2.1),
  y: 0.5 + 0.4 * Math.cos(t * (0.024 + i * 0.011) + i * 1.3),
});

/** One pre-rendered halo, reused for every lit node. */
function makeHalo(size) {
  const halo = document.createElement('canvas');
  halo.width = halo.height = size;
  const ctx = halo.getContext('2d');
  const r = size / 2;
  const gradient = ctx.createRadialGradient(r, r, 0, r, r, r);
  gradient.addColorStop(0, 'rgba(232, 213, 154, 0.9)');
  gradient.addColorStop(0.28, 'rgba(201, 168, 76, 0.42)');
  gradient.addColorStop(1, 'rgba(201, 168, 76, 0)');
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, size, size);
  return halo;
}

export default function Ambience({ zone = 'home', story, learning }) {
  const canvasRef = useRef(null);
  // Set only when motion is off, so the one static frame can be re-composed
  // when the zone or your progress changes.
  const repaintRef = useRef(null);

  const litRef = useRef(null);
  const zoneRef = useRef(zone);
  zoneRef.current = zone;

  // Read by the deferred warm-up below, which may run before or after the next
  // progress change and needs whatever is current at the moment it runs.
  const progressRef = useRef({ story, learning });
  progressRef.current = { story, learning };

  /*
   * Which nodes are lit.
   *
   * This was a `useMemo`, which reads as the careful thing to do and was the
   * opposite: `litNodes` forces the layout, the layout is sixty milliseconds of
   * relaxation, and a `useMemo` runs *during* render. Measured, that put a 52ms
   * long task at 388ms with first contentful paint at 572ms — sixty
   * milliseconds of the wait before anything appeared, spent arranging a
   * background nobody had seen yet.
   *
   * Moving it to an effect is not enough on its own: `useEffect` is deferred
   * relative to the commit, not guaranteed to be after the browser has painted.
   * The layout is therefore scheduled on an idle callback inside the canvas
   * effect, and this only recomputes once that has happened.
   */
  useEffect(() => {
    if (!litRef.current) return;
    litRef.current = litNodes(story, learning);
    repaintRef.current?.();
  }, [story, learning]);

  useEffect(() => {
    repaintRef.current?.();
  }, [zone]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext('2d', { alpha: true });
    if (!ctx) return undefined;

    const halo = makeHalo(48);
    const still = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ?? false;

    // The mesh is drawn here and blown up; the upscale is the blur.
    const mesh = document.createElement('canvas');
    const meshCtx = mesh.getContext('2d');

    const palette = ZONES[zoneRef.current] ?? ZONES.home;
    const current = palette.map(toRgb);
    const target = palette.map(toRgb);

    /*
     * The constellation arrives a beat after the colour does.
     *
     * The mesh needs nothing computed and can paint on the very first frame;
     * the graph needs sixty milliseconds of relaxation. Doing both at once
     * meant the whole ground waited on the slow half. So the loop starts
     * immediately with the mesh alone and picks the stars up whenever this
     * lands — which on any machine made this decade is the next idle moment,
     * long before anyone has read the screen they just opened.
     */
    let graph = null;
    let glow = null;
    let pulses = [];

    const warm = () => {
      graph = layout();
      glow = new Float32Array(graph.nodes.length);
      litRef.current = litNodes(progressRef.current.story, progressRef.current.learning);
      litRef.current.forEach((i) => {
        glow[i] = 1;
      });
      pulses = Array.from({ length: MAX_PULSES }, () => ({
        edge: Math.floor(Math.random() * graph.edges.length),
        t: Math.random(),
        speed: 0.06 + Math.random() * 0.1,
      }));
      if (still) repaintRef.current?.();
    };

    const whenIdle =
      window.requestIdleCallback?.bind(window) ?? ((fn) => window.setTimeout(fn, 1));
    const cancelIdle =
      window.cancelIdleCallback?.bind(window) ?? ((id) => window.clearTimeout(id));
    // The timeout is the backstop: an idle callback with nothing idle to sit in
    // would otherwise never fire, and the stars would simply never turn up.
    const warming = whenIdle(warm, { timeout: 1200 });

    let width = 0;
    let height = 0;
    let frame = null;
    let last = performance.now();
    let clock = 0;
    let sinceDraw = 0;

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      width = canvas.clientWidth;
      height = canvas.clientHeight;
      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      // Keep the upscale under about 8×: beyond that bilinear filtering starts
      // showing diamonds instead of a smooth field.
      mesh.width = Math.max(96, Math.min(240, Math.round(width / 8)));
      mesh.height = Math.max(60, Math.round((mesh.width * height) / Math.max(width, 1)));
    };

    const paintMesh = (t) => {
      const w = mesh.width;
      const h = mesh.height;
      meshCtx.clearRect(0, 0, w, h);
      meshCtx.globalCompositeOperation = 'lighter';

      for (let i = 0; i < current.length; i++) {
        const [r, g, b] = current[i];
        const at = blobAt(i, t);
        const radius = w * (0.5 + (i % 2) * 0.16);
        const gradient = meshCtx.createRadialGradient(
          at.x * w,
          at.y * h,
          0,
          at.x * w,
          at.y * h,
          radius,
        );
        gradient.addColorStop(0, `rgba(${r},${g},${b},0.85)`);
        gradient.addColorStop(1, `rgba(${r},${g},${b},0)`);
        meshCtx.fillStyle = gradient;
        meshCtx.fillRect(0, 0, w, h);
      }
      meshCtx.globalCompositeOperation = 'source-over';
    };

    const draw = (now) => {
      const dt = Math.min((now - last) / 1000, 0.1);
      last = now;
      sinceDraw += dt;
      if (sinceDraw < 1 / FPS) {
        frame = requestAnimationFrame(draw);
        return;
      }
      sinceDraw = 0;
      clock += dt;

      // Cross-fade toward the light of whatever part of the app you are in.
      const next = ZONES[zoneRef.current] ?? ZONES.home;
      for (let i = 0; i < target.length; i++) target[i] = toRgb(next[i]);
      const k = Math.min(1, dt / ZONE_FADE);
      for (let i = 0; i < current.length; i++) {
        for (let c = 0; c < 3; c++) {
          current[i][c] += (target[i][c] - current[i][c]) * k;
        }
      }

      ctx.clearRect(0, 0, width, height);

      // ── 1. the mesh ───────────────────────────────────────────────────────
      paintMesh(clock);
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.globalAlpha = 0.52;
      ctx.drawImage(mesh, 0, 0, width, height);
      ctx.globalAlpha = 1;

      // ── 2. the constellation, once it has been worked out ─────────────────
      if (!graph) {
        if (!still) frame = requestAnimationFrame(draw);
        return;
      }
      const { nodes, edges } = graph;

      // Positions first, so edges and nodes agree about where everything is.
      const px = new Float32Array(nodes.length);
      const py = new Float32Array(nodes.length);
      for (let i = 0; i < nodes.length; i++) {
        const node = nodes[i];
        const angle = node.phase + clock * node.speed;
        px[i] = (node.x + Math.cos(angle) * node.orbit) * width;
        py[i] = (node.y + Math.sin(angle * 0.8) * node.orbit) * height;

        const want = litRef.current?.has(i) ? 1 : 0;
        glow[i] += (want - glow[i]) * Math.min(1, dt * 1.6);
      }

      ctx.lineWidth = 1;
      ctx.strokeStyle = 'rgba(168, 196, 255, 0.045)';
      ctx.beginPath();
      for (const [a, b] of edges) {
        // A line between two finished things is drawn gold instead, below.
        if (glow[a] > 0.5 && glow[b] > 0.5) continue;
        ctx.moveTo(px[a], py[a]);
        ctx.lineTo(px[b], py[b]);
      }
      ctx.stroke();

      ctx.strokeStyle = 'rgba(201, 168, 76, 0.14)';
      ctx.beginPath();
      for (const [a, b] of edges) {
        if (glow[a] <= 0.5 || glow[b] <= 0.5) continue;
        ctx.moveTo(px[a], py[a]);
        ctx.lineTo(px[b], py[b]);
      }
      ctx.stroke();

      for (let i = 0; i < nodes.length; i++) {
        const node = nodes[i];
        const r = node.kind === 'chapter' ? 2.1 : 1.2;

        ctx.fillStyle = 'rgba(190, 212, 255, 0.2)';
        ctx.beginPath();
        ctx.arc(px[i], py[i], r, 0, Math.PI * 2);
        ctx.fill();

        if (glow[i] > 0.01) {
          // Kept deliberately faint. A finished run is a hundred and twenty-four
          // of these at once, and the reward for finishing the app should not be
          // a background you can no longer read over.
          const size = (node.kind === 'chapter' ? 28 : 18) * glow[i];
          ctx.globalAlpha = glow[i] * 0.5;
          ctx.drawImage(halo, px[i] - size / 2, py[i] - size / 2, size, size);
          ctx.fillStyle = 'rgba(240, 224, 176, 0.72)';
          ctx.beginPath();
          ctx.arc(px[i], py[i], r * 1.2, 0, Math.PI * 2);
          ctx.fill();
          ctx.globalAlpha = 1;
        }
      }

      // Signals running the wires. Rare and small — the thing that makes the
      // graph read as live rather than printed.
      if (!still) {
        ctx.fillStyle = 'rgba(196, 222, 255, 0.45)';
        for (const pulse of pulses) {
          pulse.t += pulse.speed * dt;
          if (pulse.t > 1) {
            pulse.t = 0;
            pulse.edge = Math.floor(Math.random() * edges.length);
            pulse.speed = 0.06 + Math.random() * 0.1;
          }
          const [a, b] = edges[pulse.edge];
          // Ease in and out so a signal arrives and fades rather than blinking.
          const fade = Math.sin(pulse.t * Math.PI);
          ctx.globalAlpha = fade * 0.7;
          ctx.beginPath();
          ctx.arc(
            px[a] + (px[b] - px[a]) * pulse.t,
            py[a] + (py[b] - py[a]) * pulse.t,
            1.5,
            0,
            Math.PI * 2,
          );
          ctx.fill();
        }
        ctx.globalAlpha = 1;
      }

      if (!still) frame = requestAnimationFrame(draw);
    };

    const start = () => {
      if (frame == null && !still) {
        last = performance.now();
        frame = requestAnimationFrame(draw);
      }
    };
    const stop = () => {
      if (frame != null) cancelAnimationFrame(frame);
      frame = null;
    };
    const onVisibility = () => (document.hidden ? stop() : start());

    // With motion off there is no loop to pick up a change, so the frame is
    // re-composed on demand — and the zone colour is snapped rather than faded,
    // since a fade needs frames it is not going to get.
    const repaint = () => {
      const next = ZONES[zoneRef.current] ?? ZONES.home;
      for (let i = 0; i < current.length; i++) current[i] = toRgb(next[i]);
      if (glow) {
        litRef.current?.forEach((i) => {
          glow[i] = 1;
        });
      }
      sinceDraw = 1;
      last = performance.now();
      draw(last);
    };

    resize();
    if (still) {
      repaintRef.current = repaint;
      repaint();
    } else {
      start();
    }

    const onResize = () => {
      resize();
      if (still) repaint();
    };

    window.addEventListener('resize', onResize);
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      stop();
      cancelIdle(warming);
      repaintRef.current = null;
      window.removeEventListener('resize', onResize);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);


  return (
    <>
      <canvas ref={canvasRef} className="ambience" aria-hidden="true" />
      <div className="ambience__grid" aria-hidden="true" />
      <div className="ambience__grain" aria-hidden="true" />
    </>
  );
}
